import TranslationManager from '@src/js/TranslationManager.js';
import sm from '@src/js/StorageManager';
import NumberFormatter from '../../NumberFormatter';

export default class MortgageForm {

    constructor({ storageLink, currency }) {

        this.props = {
            storageLink,
            currency: { iso: currency?.iso, symbol: currency?.symbol }
        };

        this.state = {

            price: {
                label: '',
                value: new NumberFormatter(0, this.props.currency.symbol),
                min: null,
                max: null,
                tags: [],
                images: [],
            },

            creditSum: {
                label: '',
                value: null,
                min: null,
                max: null,
            },

            initFee: {
                label: '',
                value: null,
                min: null,
                max: null,
                tags: [],
            },

            rate: {
                label: '',
                value: new NumberFormatter(0, '%'),
            },

            term: {
                label: '',
                value: new NumberFormatter(0),
            },

            salary: {
                label: '',
                value: null,
                min: null,
                max: null,
            },

            loanPayments: {
                label: '',
                value: null,
                min: null,
                max: null,
            },
            
            otherPayments: {
                label: '',
                value: null,
                min: null,
                max: null,
            },
            
            saving: {
                label: '',
                value: null,
                min: null,
                max: null,
            },
            
            capital: {
                label: '',
                value: null,
                min: null,
                max: null,
            },

            result: {

                insurance: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                    min: null,
                    max: null,
                },

                tax: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                    min: null,
                    max: null,
                },

                other: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                    min: null,
                    max: null,
                },

                commission: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                    min: null,
                    max: null,
                },

                requiredSalary: {
                    label: '',
                    percent: 0,
                    value: new NumberFormatter(0),
                    calculatedValue: new NumberFormatter(0, this.props.currency),
                },

                taxDeduction: {
                    label: '',
                    tooltip: null,
                    pricePercent: new NumberFormatter(0),
                    priceMaxAmount: new NumberFormatter(0),
                    overpaymentPercent: new NumberFormatter(0),
                    overpaymentMaxAmount: new NumberFormatter(0),
                    calculatedValue: new NumberFormatter(0, this.props.currency),
                },

                creditOverpayment: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                },

                payment: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                },

                paymentCreditBody: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                },

                paymentOverpayment: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                },

                creditBody: {
                    label: '',
                    value: new NumberFormatter(0, this.props.currency),
                },
            },
        };

        this.i18n = {};

        this.load();
    }

    getState() {

        return this.state;
    }

    async load() {

        if (!this.loadPromise) {
            this.loadPromise = new Promise(async (resolve) => {

                const
                    i18n = await this.loadI18n(),
                    state = await this.loadFromStorage();

                this.state.term.value.setSuffix(i18n.years);

                this.mergeStateWithSearchParams(state);

                return resolve({ i18n, state });
            });
        }
        
        return this.loadPromise;
    }

    async submit() {

        // @todo
    }

    mergeStateWithSearchParams(state) {

        const searchParams = (new URL(window.location)).searchParams;
        
        // price
        if (searchParams.has('price')) {

            // dirty time
            if (state.price == undefined) {

                const checkInterval = setInterval(() => {

                    if (state.price != undefined) {

                        clearInterval(checkInterval);
                        state.price.value.set(parseFloat(searchParams.get('price')));
                    }
                }, 250);
            }
            else {
                
                state.price.value.set(parseFloat(searchParams.get('price')));
            }
        }
        
        // rate
        if (searchParams.has('rate'))
            state.rate.value.set(parseFloat(searchParams.get('rate')));

        // term
        if (searchParams.has('creditTerm'))
            state.term.value.set(parseFloat(searchParams.get('creditTerm')));
    }

    handleResponse(query, response) {

        this.state.rate.value
            .set(query.options.rate);

        this.state.term.value
            .set(query.options.term);

        // require salary
        if (this.state.result.requiredSalary && this.state.result.requiredSalary.value.toFloat() > 0) {
            
            this.state.result.requiredSalary.calculatedValue
                .set(response.report.details.avgPayment)
                .div(this.state.result.requiredSalary.value)
                .multiple(100);
        }

        // tax deduction
        if (this.state.result.taxDeduction?.label) {

            const
                priceCalculated = new NumberFormatter(response.report.details.sumWithoutOverpayment),
                overpaymentCalculated = new NumberFormatter(response.report.details.sumOverpayment);

            priceCalculated
                .div(100)
                .multiple(this.state.result.taxDeduction.pricePercent);
            if (priceCalculated.toFloat() > this.state.result.taxDeduction.priceMaxAmount.toFloat())
                priceCalculated.set(this.state.result.taxDeduction.priceMaxAmount);

            overpaymentCalculated
                .div(100)
                .multiple(this.state.result.taxDeduction.overpaymentPercent);
            if (overpaymentCalculated.toFloat() > this.state.result.taxDeduction.overpaymentMaxAmount.toFloat())
                overpaymentCalculated.set(this.state.result.taxDeduction.overpaymentMaxAmount);

            this.state.result.taxDeduction.calculatedValue
                .set(priceCalculated)
                .plus(overpaymentCalculated);
        }

        this.state.result.creditOverpayment.value
            .set(response.report.details.sumOverpayment);

        this.state.result.payment.value
            .set(response.report.details.avgPayment);
        
        this.state.result.paymentOverpayment.value
            .set(response.report.details.avgOverpayment);

        this.state.result.paymentCreditBody.value
            .set(this.state.result.payment.value)
            .minus(this.state.result.paymentOverpayment.value);

        this.state.result.creditBody.value
            .set(response.report.details.sumWithoutOverpayment);
    }

    async loadFromStorage() {

        return new Promise((resolve) => {

            sm
                .getFrom(this.props.storageLink)
                .then(() => {

                    const
                        countryId = 'App\\Entity\\Geo\\Country',
                        calculatorId = 'App\\Entity\\Service\\Calculator',

                        inputProcessing = ({ stateName, storageName, suffix = this.props.currency, withTags = true,
                            storageTagName = null, useResult = false, withTooltip = false, willThrow = true,
                            storage = calculatorId }) => {

                                const state = useResult
                                    ? this.state.result
                                    : this.state;

                                state[stateName].label = sm.get(countryId, `${storageName}Label`, false, willThrow);

                                const properties = sm.get(storage, storageName, true, willThrow);
                                if (properties?.defaultValue != undefined) {

                                    state[stateName].value = NumberFormatter.parseEx(properties.defaultValue, {
                                        currency: suffix,
                                        decimalDelimiter: NumberFormatter.DEFAULT_DECIMAL_DELIMITER,
                                    });
                                    state[stateName].min = NumberFormatter.parseEx(properties.min, {
                                        decimalDelimiter: NumberFormatter.DEFAULT_DECIMAL_DELIMITER,
                                        allowReturnNull: true,
                                    });
                                    state[stateName].max = NumberFormatter.parseEx(properties.max, {
                                        decimalDelimiter: NumberFormatter.DEFAULT_DECIMAL_DELIMITER,
                                        allowReturnNull: true,
                                    });
                                }

                                if (withTags) {
                                    
                                    if (storageTagName == null)
                                        storageTagName = `${storageName}Tags`;

                                    state[stateName].tags = sm.get(calculatorId, storageTagName, true, false) ?? [];
                                }

                                if (withTooltip) {

                                    state[stateName].tooltip = sm.get(countryId, `${storageName}Tooltip`, false, false);
                                }
                        };

                    this.state.hasSalaryCalculator = sm.get(countryId, 'mortgageTabLabel4', false, false);

                    /** input section */

                    // price
                    inputProcessing({
                        stateName: 'price',
                        storageName: 'mortgagePrice',
                    });
                    this.state.price.images = (sm.get(countryId, 'mortgageImageCollection', true) ?? [])
                        .map((item) => ({
                            min: NumberFormatter.parseEx(item.from, {
                                decimalDelimiter: NumberFormatter.DEFAULT_DECIMAL_DELIMITER,
                            }),
                            src: item.image.link,
                        }));

                    // init fee
                    inputProcessing({
                        stateName: 'initFee',
                        storageName: 'mortgageInitFee',
                        suffix: '%',
                    });
                    
                    // term
                    inputProcessing({
                        stateName: 'term',
                        storageName: 'mortgageTerm',
                        suffix: this.i18n.years,
                    });
                    
                    // rate
                    inputProcessing({
                        stateName: 'rate',
                        storageName: 'mortgageRate',
                        suffix: '%',
                        withTags: false,
                    });

                    // credit sum
                    inputProcessing({
                        stateName: 'creditSum',
                        storageName: 'mortgageSumExtendedField',
                        storageTagName: 'mortgageSumTags',
                    });

                    const
                        getApplication = (storageId, name) => {
                            
                            const value = sm.get(storageId, name, true, false);

                            return (Object.values(value ?? {}).join('').trim() != '')
                                ? value
                                : null;
                        }, 
                        applicationByCountry = getApplication(countryId, 'mortgageApplication'),
                        applicationByType = getApplication(countryId, 'mortgageApplicationMortgage'),
                        applicationByCalc = getApplication(calculatorId, 'mortgageApplication');

                    this.state.application = applicationByCalc ?? applicationByType ?? applicationByCountry;

                    if (this.state.hasSalaryCalculator) {

                        // calculator 4: salary
                        
                        // salary
                        inputProcessing({
                            stateName: 'salary',
                            storageName: 'mortgageSalary',
                            withTags: false,
                        });

                        // loan payments
                        inputProcessing({
                            stateName: 'loanPayments',
                            storageName: 'mortgageLoanPayments',
                            withTags: false,
                        });

                        // other payments
                        inputProcessing({
                            stateName: 'otherPayments',
                            storageName: 'mortgageOtherPayments',
                            withTags: false,
                        });

                        // saving
                        inputProcessing({
                            stateName: 'saving',
                            storageName: 'mortgageSaving',
                            withTags: false,
                        });

                        // capital
                        inputProcessing({
                            stateName: 'capital',
                            storageName: 'mortgageCapital',
                            withTags: false,
                        });
                    }
                    
                    /** result section */

                    // insurance
                    inputProcessing({
                        stateName: 'insurance',
                        storageName: 'mortgageLegendInsurance',
                        withTags: false,
                        useResult: true,
                    });

                    // tax
                    inputProcessing({
                        stateName: 'tax',
                        storageName: 'mortgageLegendTax',
                        withTags: false,
                        withTooltip: true,
                        useResult: true,
                    });

                    // other
                    inputProcessing({
                        stateName: 'other',
                        storageName: 'mortgageLegendOther',
                        withTags: false,
                        withTooltip: true,
                        useResult: true,
                    });

                    // commission
                    inputProcessing({
                        stateName: 'commission',
                        storageName: 'mortgageLegendCommission',
                        withTags: false,
                        withTooltip: true,
                        useResult: true,
                        willThrow: false,
                    });

                    // required salary
                    inputProcessing({
                        stateName: 'requiredSalary',
                        storageName: 'mortgageSalaryPercent',
                        storage: countryId,
                        withTags: false,
                        withTooltip: false,
                        willThrow: false,
                        useResult: true,
                    });
                    
                    // tax deduction
                    this.state.result.taxDeduction.label = sm.get(countryId, 'mortgageTaxDeductionLabel', false, false);
                    this.state.result.taxDeduction.tooltip = sm.get(countryId, 'mortgageTaxDeductionTooltip', false, false);
                    this.state.result.taxDeduction.pricePercent.set(sm.get(countryId, 'mortgageTaxDeductionPricePercent', false, false));
                    this.state.result.taxDeduction.priceMaxAmount.set(sm.get(countryId, 'mortgageTaxDeductionPriceMaxAmount', false, false));
                    this.state.result.taxDeduction.overpaymentPercent.set(sm.get(countryId, 'mortgageTaxDeductionOverpaymentPercent', false, false));
                    this.state.result.taxDeduction.overpaymentMaxAmount.set(sm.get(countryId, 'mortgageTaxDeductionOverpaymentMaxAmount', false, false));
                    
                    // payment
                    this.state.result.payment.label = sm.get(countryId, 'mortgageChartPaymentsLabel');
                    
                    // payment credit body
                    this.state.result.paymentCreditBody.label = sm.get(countryId, 'mortgageLegendCreditBodyLabel');
                    
                    // payment overpayment
                    this.state.result.paymentOverpayment.label = sm.get(countryId, 'mortgageLegendPercentLabel');
                    
                    // credit body
                    this.state.result.creditBody.label = sm.get(countryId, 'mortgageLegendCreditBodyLabel');
                    
                    // credit overpayment
                    this.state.result.creditOverpayment.label = sm.get(countryId, 'mortgageLegendPercentLabel');

                    resolve({
                        rate: this.state.rate,
                        term: this.state.term,
                        result: this.state.result,
                    });
                });
        });
    }

    async loadI18n() {

        return new Promise((resolve) => {

            TranslationManager
                .get('years')
                .then((translation) => {

                    this.i18n = { years: translation };
                    resolve(this.i18n);
                });
        });
    }
}